import React, { useMemo, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Stepper,
  Step,
  StepLabel,
  Box,
  Paper,
  Button,
  Stack,
  Snackbar,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import UploadStep from './components/UploadStep.jsx';
import GalleryStep from './components/GalleryStep.jsx';
import ExportStep from './components/ExportStep.jsx';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InfoIcon from '@mui/icons-material/Info';
import { showModalDialog } from './utils/ShowModalDialog.jsx';

const steps = ['Upload', 'Gallery', 'Export'];

function App() {
  const [activeStep, setActiveStep] = useState(0);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [pages, setPages] = useState([]);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  const canProceed = useMemo(() => {
    if (activeStep === 0) return !!pdfBytes && pages.length > 0;
    if (activeStep === 1) return pages.length > 0;
    return true;
  }, [activeStep, pdfBytes, pages.length]);

  const resetAll = () => {
    setPdfBytes(null);
    setPages([]);
    setActiveStep(0);
    setSnack({ open: true, msg: 'Reset complete', sev: 'info' });
  };

  const showAbout = async () => {
    await showModalDialog({
      title: 'About PDF Splitter',
      type: 'information',
      buttonType: 'ok',
      body: (
        <div>
          <Typography gutterBottom>
            Split, reorder, and export pages from a PDF — all locally in your browser.
          </Typography>
          <ul style={{ marginTop: 0 }}>
            <li>Select a PDF and preview page thumbnails</li>
            <li>Reorder pages via drag & drop</li>
            <li>Export a single page as PDF or PNG</li>
            <li>Bulk export: ZIP of PDFs or PNGs</li>
            <li>Merge selected pages into a new PDF</li>
            <li>No uploads — processing happens entirely on-device</li>
          </ul>
          <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
            Used libraries/license info: React, Material UI, Emotion, PDF.js, pdf-lib, JSZip, DnD Kit — all libraries are licensed under the MIT License (PDF.js: Apache-2.0).
          </Typography>
        </div>
      ),
    });
  };

  const backNextButtons = () => (
    <>
      <Button
        variant="outlined"
        disabled={activeStep === 0}
        onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
      >
        Back
      </Button>
      <Box sx={{ flexGrow: 1 }} />
      <Button
        variant="contained"
        disabled={!canProceed || activeStep === steps.length - 1}
        onClick={() => setActiveStep((s) => Math.min(steps.length - 1, s + 1))}
      >
        Next
      </Button>
    </>
  )

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="sticky" color="default" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            PDF Splitter
          </Typography>
          <IconButton color="inherit" onClick={showAbout} title="About">
            <InfoIcon />
          </IconButton>
          <IconButton color="inherit" onClick={resetAll} title="Reset">
            <RestartAltIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            {backNextButtons()}
          </Stack>

          <Divider sx={{ my: 3 }} />

          {activeStep === 0 && (
            <UploadStep
              onLoaded={(bytes, generatedPages) => {
                setPdfBytes(bytes);
                setPages(generatedPages);
                setSnack({
                  open: true,
                  msg: `Loaded ${generatedPages.length} pages`,
                  sev: 'success',
                });
                setActiveStep(1); // auto-advance to Gallery after upload
              }}
            />
          )}

          {activeStep === 1 && (
            <GalleryStep pages={pages} setPages={setPages} />
          )}

          {activeStep === 2 && (
            <ExportStep pdfBytes={pdfBytes} pages={pages} />
          )}

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            {backNextButtons()}
          </Stack>
        </Paper>
      </Container>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((x) => ({ ...x, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} variant="filled" sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default App;
